import { useMutation } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'
import { RlcpUploadFailureResponseDtoSchema } from '../api/data-connectors/dtos/rlcp'
import { uploadRlcpFiles } from '../api/data-connectors/rlcp-files'
import { fileStatusService } from '../services/file-status-service'
import { addLoadingFile, removeLoadingFile } from '../services/loading-files-service'
import type { FileInfoType, SelectedRlcpFile } from '../types'
import { getMimeTypeFromExtension } from '../utils'
import { useNotification } from './useNotification'

export function useUploadRlcpFiles(onComplete: () => void) {
  const { notify } = useNotification()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: (files: SelectedRlcpFile[]) => uploadRlcpFiles(files),
    onSuccess: async (data) => {
      if (data.length) {
        data.forEach((file: FileInfoType) => {
          addLoadingFile(file.id, file.name, getMimeTypeFromExtension(file.type))
          fileStatusService.startPolling(file.id, () => removeLoadingFile(file.id), notify, t)
        })
        onComplete()
      }
    },
    onError: (error) => {
      const parsed = RlcpUploadFailureResponseDtoSchema.safeParse(
        (error as AxiosError).response?.data
      )
      const failures = parsed.success ? parsed.data.detail.failures : undefined
      if (failures?.length) {
        failures.forEach((failure) => {
          console.error('File upload failure', failure.detail)
          notify({
            isSuccess: false,
            text: t('files.uploadFailureToastForFile', {
              filename: failure.filename,
            }),
            position: 'bottom',
          })
        })
      } else {
        console.error('error in useUploadRlcpFiles', error)
        notify({ isSuccess: false, text: t('files.multiUploadFailureToast'), position: 'bottom' })
      }
    },
  })
}
